import axios from "axios";
import { cloneDeep } from "lodash";
import { setApps } from "../../../../../reducers/appReducer";
import moment from "moment";
import { getAccessToken } from "../../../../../components/powerBI/components/report/reducers/reportReducer";
import { getClientToken, getSessionClaims } from "../../../../../lib/authMode";

/* -------------------- INITIAL STATE -------------------- */
const INITIAL_STATE = {
  activeReport: {},
  activeWS: {},
  loading: false,
  loaded: false,
  appMenuLoading: false,
  appMenuLoaded: false,
  error: null,
  workspaces: [],
  searchWorkspaces: [],
  newRptSearchWorkspaces: [],
  appMenu: [],
  searchAppMenu: [],
  activeDataset: -1,
  favorites: [],
  recents: [],
  featured: [],
  sFavorites: [],
  // Which reportGroup sFavorites currently corresponds to — lets a component
  // that needs favorites for the active workspace (e.g. powerbi-report) skip
  // re-fetching when reportIconList (or a prior mount) already fetched it.
  sFavoritesReportGroup: null,
  sRecents: [],
  sFeatured: [],
  displayRpts: [],
  sDisplayRpts: [],
  displayFiles: [],
  sDisplayFiles: [],
  originalFeatured: [],
  iconListLoaded: false,
  iconListLoading: false,
  newsFeed: [],
  reportView: "default",
  tokens: null,
  homePageReportingApps: null,
  hPRAppsFolderStatus: null,
  dashboardReports: null,
};

/* -------------------- ACTION TYPES -------------------- */
const SET_ACTIVE_RPT = "SET_ACTIVE_RPT";
const SET_WORKSPACES = "SET_WORKSPACES";
const ADD_WORKSPACE_REPORTS = "ADD_WORKSPACE_REPORTS";
// const SET_RPTCFG = "SET_RPTCFG";
const FETCH_REPORT_LIST_BEGIN = "FETCH_REPORT_LIST_BEGIN";
const FETCH_REPORT_LIST_SUCCESS = "FETCH_REPORT_LIST_SUCCESS";
const FETCH_REPORT_LIST_FAILURE = "FETCH_REPORT_LIST_FAILURE";
const FETCH_APP_MENU_BEGIN = "FETCH_APP_MENU_BEGIN";
const FETCH_APP_MENU_SUCCESS = "FETCH_APP_MENU_SUCCESS";
const FETCH_APP_MENU_FAILURE = "FETCH_APP_MENU_FAILURE";
const FETCH_LANDING_PAGE_BEGIN = "FETCH_LANDING_PAGE_BEGIN";
const FETCH_LANDING_PAGE_SUCCESS = "FETCH_LANDING_PAGE_SUCCESS";
const FETCH_LANDING_PAGE_FAILURE = "FETCH_LANDING_PAGE_FAILURE";
const SET_SEARCH_WS = "SET_SEARCH_WS";
const DELETE_REPORT_SUCCESS = "DELETE_REPORT_SUCCESS";
const SET_NEW_RPT_SEARCH_WS = "SET_NEW_RPT_SEARCH_WS";
const SET_SEARCH_LANDING_PAGE = "SET_SEARCH_LANDING_PAGE";
const SET_NEWS_FEED = "SET_NEWS_FEED";
const SET_REPORT_VIEW = "SET_REPORT_VIEW";
const SET_ACTIVE_WS = "SET_ACTIVE_WS";
const SET_DISPLAYED_FEATURED_REPORTS = "SET_DISPLAYED_FEATURED_REPORTS";
const GET_RECENT_AND_FAV_REPORTS = "GET_RECENT_AND_FAV_REPORTS";
const SET_FAVORITE_RPT = "SET_FAVORITE_RPT";
const SET_DISPLAYED_REPORTS = "SET_DISPLAYED_REPORTS";
const SET_REPORTING_APPS = "SET_REPORTING_APPS";
const SET_RA_FOLDER_STATUS = "SET_RA_FOLDER_STATUS";
const SET_DASHBOARD_RPTS = "SET_DASHBOARD_RPTS";
const SET_DISPLAYED_FILES = "SET_DISPLAYED_FILES";

/* -------------------- ACTION CREATORS -------------------- */
const doSetReportView = (view) => {
  return { type: SET_REPORT_VIEW, payload: view };
};
const set_active_rpt = (activeWS, rIndex, dataset) => {
  return {
    type: SET_ACTIVE_RPT,
    activeReport: rIndex,
    activeWS: activeWS,
    activeDataset: dataset,
  };
};

const set_active_ws = (activeWS) => {
  return {
    type: SET_ACTIVE_WS,
    activeWS,
  };
};
const set_displayed_featured_reports = (featured) => {
  return {
    type: SET_DISPLAYED_FEATURED_REPORTS,
    featured,
  };
};
const get_recent_and_fav_reports = (recents, favs, reportGroup) => {
  return {
    type: GET_RECENT_AND_FAV_REPORTS,
    recents,
    favs,
    reportGroup,
  };
};

const fetchReportListBegin = () => ({
  type: FETCH_REPORT_LIST_BEGIN,
});
// const fetchReportListSuccess = () => {
//   return { type: FETCH_REPORT_LIST_SUCCESS };
// };
const setWorkSpaces = (workspaces, favs, tokens, files) => {
  return { type: SET_WORKSPACES, payload: workspaces, favorites: favs, tokens, files };
};
const addWorkSpaceReport = (report) => {
  return { type: ADD_WORKSPACE_REPORTS, report };
};
const setSearchWorkspace = (workspaces) => {
  return { type: SET_SEARCH_WS, payload: workspaces };
};
const setSearchLandingPage = (favorites, recents, featured, displayRpts, displayFiles) => {
  return {
    type: SET_SEARCH_LANDING_PAGE,
    favorites,
    recents,
    featured,
    displayRpts,
    displayFiles,
  };
};
const setNewRptSearchWorkspace = (workspaces) => {
  return { type: SET_NEW_RPT_SEARCH_WS, payload: workspaces };
};

const toComparableId = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return String(value);
};

const getWorkspaceId = (item) => {
  if (!item) {
    return undefined;
  }

  if (item.workspaceId) {
    return toComparableId(item.workspaceId);
  }

  if (item.workspace && typeof item.workspace === "object") {
    return toComparableId(item.workspace.workspaceId || item.workspace._id || item.workspace.id);
  }

  if (item.workspace && typeof item.workspace === "string") {
    return toComparableId(item.workspace);
  }

  return undefined;
};

const getReportIds = (item) => {
  const report = item && item.report && typeof item.report === "object" ? item.report : undefined;
  return [
    toComparableId(item && item.id),
    toComparableId(item && item._id),
    toComparableId(item && item.reportId),
    toComparableId(report && report.id),
    toComparableId(report && report._id),
    toComparableId(report && report.reportId),
  ].filter(Boolean);
};

const removeDeletedReportFromCollection = (collection, deletedReportId, deletedWorkspaceId) => {
  if (!Array.isArray(collection) || !deletedReportId) {
    return collection;
  }

  const comparableReportId = toComparableId(deletedReportId);
  const comparableWorkspaceId = toComparableId(deletedWorkspaceId);

  return collection.filter((item) => {
    const reportMatches = getReportIds(item).includes(comparableReportId);
    if (!reportMatches) {
      return true;
    }

    if (!comparableWorkspaceId) {
      return false;
    }

    const itemWorkspaceId = getWorkspaceId(item);
    if (!itemWorkspaceId) {
      return false;
    }

    return itemWorkspaceId !== comparableWorkspaceId;
  });
};

const fetchReportListError = (error) => ({
  type: FETCH_REPORT_LIST_FAILURE,
  payload: { error },
});

const deleteReportSuccess = (workspace, deletedReportId, deletedWorkspaceId) => {
  return { type: DELETE_REPORT_SUCCESS, payload: workspace, deletedReportId, deletedWorkspaceId };
};
const fetchLandingPageBegin = () => ({
  type: FETCH_LANDING_PAGE_BEGIN,
});
const fetchLandingPageSuccess = (featured, sasTokens) => {
  return {
    type: FETCH_LANDING_PAGE_SUCCESS,
    featured,
    tokens: sasTokens,
  };
};
const fetchAppMenuError = () => ({
  type: FETCH_APP_MENU_FAILURE,
});
const fetchAppMenuBegin = () => ({
  type: FETCH_APP_MENU_BEGIN,
});
// const fetchAppMenuSuccess = menu => {
//   return {
//     type: FETCH_APP_MENU_SUCCESS,
//     payload: menu
//   };
// };
const fetchLandingPageError = () => ({
  type: FETCH_LANDING_PAGE_FAILURE,
});

const setNewsFeed = (newsFeed) => {
  return { type: SET_NEWS_FEED, newsFeed };
};
const setFavorite = (favorite, remove) => {
  return { type: SET_FAVORITE_RPT, payload: favorite, remove };
};
const setReportingApps = (apps) => {
  return { type: SET_REPORTING_APPS, payload: apps };
};
const setDashboardReports = (rpts) => {
  return { type: SET_DASHBOARD_RPTS, payload: rpts };
};

// const setRAFolderStatus = folderStatus => {
//   return { type: SET_RA_FOLDER_STATUS, payload: folderStatus };
// };

/* -------------------- ASYNC ACTION CREATORS -------------------- */
const _loadingGroups = new Set();
let _reportsLoaded = false;

export const getReports = (reportGroup = "", ws = "", rpt = "", showFilterPane = false) => {
  return (dispatch) => {
    if (ws === "" && rpt === "") {
      if (_reportsLoaded) {
        return;
      }
      if (_loadingGroups.has(reportGroup)) {
        return;
      }
      _loadingGroups.add(reportGroup);
    }
    dispatch(fetchReportListBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getUpdateRptTimes`,
        { reportGroup },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((res) => {})
      .catch((error) => {
        // Silently fail if server is unreachable (e.g., during local development)
      });
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/getReports`,
        { reportGroup },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        if (ws !== "") {
          axios
            .post(
              `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/updatePermissions`,
              {
                groupId: ws,
                reportId: rpt,
                showFilterPane,
                //idToken: localStorage.getItem("id_token")
              },
              {
                headers: {
                  ClientToken: localStorage.getItem("clientToken"),
                },
              },
            )
            .then((result) => {
              dispatch(addWorkSpaceReport(result.data.response));
            });
        } else {
          dispatch(
            setWorkSpaces(response.data.workspaces, response.data.favorites, response.data.sasTokens, response.data.file),
          );

          dispatch(setApps(response.data.workspaces));
          _reportsLoaded = true;
        }
        dispatch(setDashboardReports(response.data.dashboardRpts));
        dispatch(setReportingApps(response.data.homepageApps));

        if (response.data.homepageApps !== undefined && response.data.homepageApps !== null) {
          axios
            .get(
              `${process.env.REACT_APP_BIGHORN_SERVER}/api/app/getHomePageAppStatus`,

              {
                headers: {
                  ClientToken: localStorage.getItem("clientToken"),
                },
              },
            )
            .then((response) => {
              // dispatch(setRAFolderStatus(response.data.homepageAppOpenStatus));
            });
        }
        //dispatch(fetchReportListSuccess());
      })
      .catch((error) => {
        dispatch(fetchReportListError(error));
      })
      .finally(() => {
        if (ws === "" && rpt === "") {
          _loadingGroups.delete(reportGroup);
        }
      });
  };
};

// Force a full workspace refresh, bypassing the _reportsLoaded dedup flag.
// Use this after clone/create operations where the new report must be indexed immediately.
export const forceRefreshReports = (reportGroup = "") => {
  return (dispatch) => {
    _reportsLoaded = false;
    dispatch(getReports(reportGroup));
  };
};

export const getAppMenu = () => {
  return (dispatch) => {
    dispatch(fetchAppMenuBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/getAppMenu`,
        {},
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        dispatch(setWorkSpaces(response.data.workspaces));
        dispatch(setApps(response.data.workspaces));
      })
      .catch((err) => {
        dispatch(fetchAppMenuError());
      });
  };
};
export const getLandingPage = () => {
  return (dispatch) => {
    dispatch(fetchLandingPageBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/getLandingPage`,
        {},
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        let sasTokens = response.data.sasTokens;
        // let favs = response.data.favorites.map(fav => {
        //   if (fav.img !== "") {
        //     let ws = fav.workspace.name
        //       .toLowerCase()
        //       .replace(/[^a-zA-Z0-9]*/g, "");
        //     sasTokens.forEach(token => {
        //       if (token.workspaceName === ws) {
        //         fav.img = fav.img + "?" + token.sasToken;
        //       }
        //     });
        //   }
        //   return fav;
        // });

        // let recent = response.data.recents.map(rec => {
        //   if (rec.img !== "") {
        //     let ws = rec.workspace.name
        //       .toLowerCase()
        //       .replace(/[^a-zA-Z0-9]*/g, "");
        //     sasTokens.forEach(token => {
        //       if (token.workspaceName === ws) {
        //         rec.img = rec.img + "?" + token.sasToken;
        //       }
        //     });
        //   }
        //   return rec;
        // });
        let featured = response.data.featured.map((feat) => {
          if (feat.img !== "") {
            let ws = feat.workspace.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");

            sasTokens.forEach((token) => {
              if (token.workspaceName === ws && !feat.img.includes("?st=")) {
                feat.img = feat.img + "?" + token.sasToken;
              } else if (token.workspaceName === ws) {
                feat.img = feat.img.split("?st=")[0] + "?" + token.sasToken;
              }
            });
          }
          return feat;
        });

        dispatch(fetchLandingPageSuccess(featured, sasTokens));
      })
      .catch((error) => {
        dispatch(fetchLandingPageError(error));
      });
  };
};

export const saveFavorite = (fav, remove) => {
  return (dispatch) => {
    // Bumped synchronously at issue time, not in .then() below — a
    // getRecentAndFavReports read already in flight (or one issued and
    // resolved entirely within this save's own network round trip) must be
    // treated as stale from the moment this save starts, not only once its
    // response arrives. Waiting for the response left a real gap: a
    // background read that both starts and finishes while this save's POST is
    // still in flight would otherwise still pass the version check (nothing
    // had bumped it yet) and overwrite sFavorites with pre-save data.
    favoritesRecentsVersion += 1;
    // Returned (not left dangling) and no longer swallowing the error here —
    // callers (e.g. powerbi-report's addFavorites) need to know whether this
    // actually saved before applying it optimistically, so a rejection has to
    // reach them rather than be silently absorbed.
    return axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/saveFavorites`,
        {
          fav,
          remove,
          //user: localStorage.getItem("user")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((result) => {
        dispatch(setFavorite(fav, remove));
      });
  };
};
export const addRecent = (recent) => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/addRecents`,
        {
          recent,
          //user: localStorage.getItem("user")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((result) => {
        // Note: Removed getLandingPage() call here to avoid duplicate API calls.
        // The landing page data is already loaded on initial page load.
      })
      .catch((err) => {
        console.warn("addRecent failed:", err?.response?.status || err?.message);
      });
  };
};

export const saveUserReportView = (reportId, view) => {
  return (dispatch) => {
    dispatch(doSetReportView(view));
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/saveUserReportView`,
        {
          reportId,
          view,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {})
      .catch((error) => console.log(error));
  };
};
export const getReportViewForUser = (reportId) => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getReportViewForUser`,
        {
          reportId,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        if (response.data.override !== undefined) {
          dispatch(doSetReportView(response.data.override.view));
        }
      })
      .catch((error) => console.log(error));
  };
};
export const updatePermissions = (ws = "", rpt = "", showFilterPane = false) => {
  return (dispatch) => {
    dispatch(fetchReportListBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/updatePermissions`,
        {
          groupId: ws,
          reportId: rpt,
          showFilterPane,
          //idToken: localStorage.getItem("id_token")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((result) => {
        axios.post(
          `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getWorkspacesAndReportsByName`,
          {
            //clientToken: localStorage.getItem("clientToken")
          },
          {
            headers: {
              ClientToken: localStorage.getItem("clientToken"),
            },
          },
        );
      })
      .then((response) => {
        dispatch(setWorkSpaces(response.data.workspaces));
        dispatch(setApps(response.data.workspaces));
      })
      .catch((error) => {
        dispatch(fetchReportListError(error));
      });
  };
};

// Returns true when the item's name or description contains the (case-insensitive) filter text.
const itemMatchesText = (item, filter) => {
  if (!item) {
    return false;
  }
  const lc = filter.toLowerCase();
  const nameMatch = typeof item.name === "string" && item.name.toLowerCase().indexOf(lc) !== -1;
  // Files may carry their display name in `title` rather than `name`.
  const titleMatch = typeof item.title === "string" && item.title.toLowerCase().indexOf(lc) !== -1;
  const descMatch =
    typeof item.description === "string" &&
    item.description !== null &&
    item.description.toLowerCase().indexOf(lc) !== -1;
  return nameMatch || titleMatch || descMatch;
};

// Recursively collects items (reports and folders) that match the filter, walking
// folder/subfolder children to arbitrary depth. Every matching item is surfaced,
// including a folder and a same-named report nested within it — a matching folder is
// still recursed into so nested matches are not hidden. Used by the icon view, which
// renders results as a flat list of tiles.
const collectMatchingItems = (items, filter) => {
  if (!Array.isArray(items)) {
    return [];
  }
  let results = [];
  items.forEach((item) => {
    if (itemMatchesText(item, filter)) {
      results.push(item);
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      results = results.concat(collectMatchingItems(item.children, filter));
    }
  });
  return results;
};

// Recursively filters a workspace's children tree, preserving the folder/subfolder
// structure to arbitrary depth. A folder is kept (with its full contents) when it
// matches directly; otherwise its children are filtered recursively and the folder is
// kept only if any descendant survived. Used by the list view, which renders a tree.
const filterWorkspaceChildren = (children, filter) => {
  if (!Array.isArray(children)) {
    return [];
  }
  return children.filter((node) => {
    if (Array.isArray(node.children)) {
      if (itemMatchesText(node, filter)) {
        return true;
      }
      node.children = filterWorkspaceChildren(node.children, filter);
      return node.children.length > 0;
    }
    return itemMatchesText(node, filter);
  });
};

export const filterReports = (filter, workspaces) => {
  return (dispatch) => {
    if (filter === "") {
      dispatch(setSearchWorkspace(workspaces));
    } else {
      let newWs = cloneDeep(workspaces);

      let date;
      if (filter.includes("|||")) {
        let filterArr = filter.split("|||");
        filter = filterArr[0];
        date = filterArr[1];
      }
      let dateField, dateStr;
      if (date === "null" || date === undefined) {
        dispatch(
          setSearchWorkspace(
            newWs.map((workspace) => {
              if (workspace.children === undefined) {
                workspace.reports = workspace.reports.filter((report) => {
                  return itemMatchesText(report, filter);
                });
              } else {
                workspace.children = filterWorkspaceChildren(workspace.children, filter);
              }

              return workspace;
            }),
          ),
        );
      } else if (filter === "") {
        dateStr = date.split(" ")[3];
        if (date.includes("Created Date")) {
          dateField = "pbiCreatedDate";
        } else {
          dateField = "pbiModifiedDate";
        }
        if (date.includes("After")) {
          dispatch(
            setSearchWorkspace(
              newWs.map((workspace) => {
                if (workspace.children === undefined) {
                  workspace.reports = workspace.reports.filter((report) => {
                    return moment(report[dateField]).isAfter(dateStr);
                  });
                } else {
                  workspace.children = workspace.children.filter((file, index) => {
                    if (file.children === undefined) {
                      if (file[dateField] !== undefined && file[dateField] !== null) {
                        return moment(file[dateField]).isAfter(dateStr);
                      } else {
                        return false;
                      }
                    } else {
                      file.children = file.children.filter((child) => {
                        if (child.fileType === "folder") {
                          child.children = child.children.filter((gChild) => {
                            if (gChild[dateField] !== undefined && gChild[dateField] !== null) {
                              return moment(gChild[dateField]).isAfter(dateStr);
                            } else {
                              return false;
                            }
                          });
                          if (child.children.length > 0) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          if (child[dateField] !== undefined && child[dateField] !== null) {
                            return moment(child[dateField]).isAfter(dateStr);
                          } else {
                            return false;
                          }
                        }
                      });
                      if (file.children.length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    }
                  });
                }

                return workspace;
              }),
            ),
          );
        } else {
          dispatch(
            setSearchWorkspace(
              newWs.map((workspace) => {
                if (workspace.children === undefined) {
                  workspace.reports = workspace.reports.filter((report) => {
                    return moment(report[dateField]).isBefore(dateStr);
                  });
                } else {
                  workspace.children = workspace.children.filter((file, index) => {
                    if (file.children === undefined) {
                      if (file[dateField] !== undefined && file[dateField] !== null) {
                        return moment(file[dateField]).isBefore(dateStr);
                      } else {
                        return false;
                      }
                    } else {
                      file.children = file.children.filter((child) => {
                        if (child.fileType === "folder") {
                          child.children = child.children.filter((gChild) => {
                            if (gChild[dateField] !== undefined && gChild[dateField] !== null) {
                              return moment(gChild[dateField]).isBefore(dateStr);
                            } else {
                              return false;
                            }
                          });
                          if (child.children.length > 0) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          if (child[dateField] !== undefined && child[dateField] !== null) {
                            return moment(child[dateField]).isBefore(dateStr);
                          } else {
                            return false;
                          }
                        }
                      });
                      if (file.children.length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    }
                  });
                }

                return workspace;
              }),
            ),
          );
        }
      } else {
        dateStr = date.split(" ")[3];
        if (date.includes("Created Date")) {
          dateField = "pbiCreatedDate";
        } else {
          dateField = "pbiModifiedDate";
        }
        if (date.includes("After")) {
          dispatch(
            setSearchWorkspace(
              newWs.map((workspace) => {
                if (workspace.children === undefined) {
                  workspace.reports = workspace.reports.filter((report) => {
                    return (
                      moment(report[dateField]).isAfter(dateStr) &&
                      report.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
                    );
                  });
                } else {
                  workspace.children = workspace.children.filter((file, index) => {
                    if (file.children === undefined) {
                      if (file[dateField] !== undefined && file[dateField] !== null) {
                        return (
                          moment(file[dateField]).isAfter(dateStr) &&
                          file.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
                        );
                      } else {
                        return false;
                      }
                    } else {
                      file.children = file.children.filter((child) => {
                        if (child.fileType === "folder") {
                          child.children = child.children.filter((gChild) => {
                            if (gChild[dateField] !== undefined && gChild[dateField] !== null) {
                              return (
                                moment(gChild[dateField]).isAfter(dateStr) &&
                                gChild.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
                              );
                            } else {
                              return false;
                            }
                          });
                          if (child.children.length > 0) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          if (child[dateField] !== undefined && child[dateField] !== null) {
                            return (
                              moment(child[dateField]).isAfter(dateStr) &&
                              child.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
                            );
                          } else {
                            return false;
                          }
                        }
                      });
                      if (file.children.length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    }
                  });
                }

                return workspace;
              }),
            ),
          );
        } else {
          dispatch(
            setSearchWorkspace(
              newWs.map((workspace) => {
                if (workspace.children === undefined) {
                  workspace.reports = workspace.reports.filter((report) => {
                    return moment(report[dateField]).isBefore(dateStr);
                  });
                } else {
                  workspace.children = workspace.children.filter((file, index) => {
                    if (file.children === undefined) {
                      if (file[dateField] !== undefined && file[dateField] !== null) {
                        return moment(file[dateField]).isBefore(dateStr);
                      } else {
                        return false;
                      }
                    } else {
                      file.children = file.children.filter((child) => {
                        if (child.fileType === "folder") {
                          child.children = child.children.filter((gChild) => {
                            if (gChild[dateField] !== undefined && gChild[dateField] !== null) {
                              return moment(gChild[dateField]).isBefore(dateStr);
                            } else {
                              return false;
                            }
                          });
                          if (child.children.length > 0) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          if (child[dateField] !== undefined && child[dateField] !== null) {
                            return moment(child[dateField]).isBefore(dateStr);
                          } else {
                            return false;
                          }
                        }
                      });
                      if (file.children.length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    }
                  });
                }

                return workspace;
              }),
            ),
          );
        }
      }
    }
  };
};

export const filterLandingPage = (filter, fav, recents, featured, displayRpts, displayFiles) => {
  return (dispatch) => {
    if (filter === "") {
      dispatch(setSearchLandingPage(fav, recents, featured, displayRpts, displayFiles));
    } else {
      let date;
      if (filter.includes("|||")) {
        let filterArr = filter.split("|||");
        filter = filterArr[0];
        date = filterArr[1];
      }

      let newFavs = cloneDeep(fav);
      let newRecents = cloneDeep(recents);
      let newFeatured = cloneDeep(featured);
      let newDisplayRpts = cloneDeep(displayRpts);
      let newDisplayFiles = cloneDeep(displayFiles);
      let dateField;
      if (date === "null" || date === undefined) {
        dispatch(
          setSearchLandingPage(
            newFavs.filter((nFav) => {
              return nFav.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            }),
            newRecents.filter((nRecents) => {
              return nRecents.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            }),
            newFeatured.filter((nFeatured) => {
              return nFeatured.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            }),
            collectMatchingItems(newDisplayRpts, filter),
            collectMatchingItems(newDisplayFiles, filter),
          ),
        );
      } else if (filter === "") {
        if (date.includes("Created Date")) {
          dateField = "pbiCreatedDate";
        } else {
          dateField = "pbiModifiedDate";
        }
        if (date.includes("After")) {
          dispatch(
            setSearchLandingPage(
              newFavs.filter((nFav) => {
                if (nFav[dateField] === undefined) {
                  return false;
                }
                return moment(nFav[dateField]).isAfter(date.split(" ")[3]);
              }),
              newRecents.filter((nRecents) => {
                if (nRecents[dateField] === undefined) {
                  return false;
                }
                return moment(nRecents[dateField]).isAfter(date.split(" ")[3]);
              }),
              newFeatured.filter((nFeatured) => {
                if (nFeatured[dateField] === undefined) {
                  return false;
                }
                return moment(nFeatured[dateField]).isAfter(date.split(" ")[3]);
              }),
              newDisplayRpts.filter((nDisplay) => {
                if (nDisplay[dateField] === undefined) {
                  return false;
                }
                return moment(nDisplay[dateField]).isAfter(date.split(" ")[3]);
              }),
              newDisplayFiles,
            ),
          );
        } else {
          dispatch(
            setSearchLandingPage(
              newFavs.filter((nFav) => {
                if (nFav[dateField] === undefined) {
                  return false;
                }
                return moment(nFav[dateField]).isBefore(date.split(" ")[3]);
              }),
              newRecents.filter((nRecents) => {
                if (nRecents[dateField] === undefined) {
                  return false;
                }
                return moment(nRecents[dateField]).isBefore(date.split(" ")[3]);
              }),
              newFeatured.filter((nFeatured) => {
                if (nFeatured[dateField] === undefined) {
                  return false;
                }
                return moment(nFeatured[dateField]).isBefore(date.split(" ")[3]);
              }),
              newDisplayRpts.filter((nDisplay) => {
                if (nDisplay[dateField] === undefined) {
                  return false;
                }
                return moment(nDisplay[dateField]).isBefore(date.split(" ")[3]);
              }),
              newDisplayFiles,
            ),
          );
        }
      } else {
        if (date.includes("Created Date")) {
          dateField = "pbiCreatedDate";
        } else {
          dateField = "pbiModifiedDate";
        }
        let dateStr = date.split(" ")[3];
        if (date.includes("After")) {
          dispatch(
            setSearchLandingPage(
              newFavs.filter((nFav) => {
                if (nFav[dateField] === undefined) {
                  return false;
                }
                return nFav.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 && moment(nFav[dateField]).isAfter(dateStr);
              }),
              newRecents.filter((nRecents) => {
                if (nRecents[dateField] === undefined) {
                  return false;
                }
                return (
                  nRecents.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nRecents[dateField]).isAfter(dateStr)
                );
              }),
              newFeatured.filter((nFeatured) => {
                if (nFeatured[dateField] === undefined) {
                  return false;
                }
                return (
                  nFeatured.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nFeatured[dateField]).isAfter(dateStr)
                );
              }),
              newDisplayRpts.filter((nDisplay) => {
                if (nDisplay[dateField] === undefined) {
                  return false;
                }
                return (
                  nDisplay.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nDisplay[dateField]).isAfter(dateStr)
                );
              }),
              collectMatchingItems(newDisplayFiles, filter),
            ),
          );
        } else {
          dispatch(
            setSearchLandingPage(
              newFavs.filter((nFav) => {
                if (nFav[dateField] === undefined) {
                  return false;
                }
                return (
                  nFav.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 && moment(nFav[dateField]).isBefore(dateStr)
                );
              }),
              newRecents.filter((nRecents) => {
                if (nRecents[dateField] === undefined) {
                  return false;
                }
                return (
                  nRecents.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nRecents[dateField]).isBefore(dateStr)
                );
              }),
              newFeatured.filter((nFeatured) => {
                if (nFeatured[dateField] === undefined) {
                  return false;
                }
                return (
                  nFeatured.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nFeatured[dateField]).isBefore(dateStr)
                );
              }),
              newDisplayRpts.filter((nDisplay) => {
                if (nDisplay[dateField] === undefined) {
                  return false;
                }
                return (
                  nDisplay.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 &&
                  moment(nDisplay[dateField]).isBefore(dateStr)
                );
              }),
              collectMatchingItems(newDisplayFiles, filter),
            ),
          );
        }
      }
    }
  };
};

export const filterDatasets = (filter, workspaces) => {
  return (dispatch) => {
    if (filter === "") {
      dispatch(setNewRptSearchWorkspace(workspaces));
    } else {
      let newWs = cloneDeep(workspaces);
      dispatch(
        setNewRptSearchWorkspace(
          newWs.map((workspace) => {
            workspace.datasets = workspace.datasets.filter((dataset) => {
              return dataset.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            });
            return workspace;
          }),
        ),
      );
    }
  };
};

export const setActiveReport = (wIndex, rIndex, dataset = -1, fetchToken = true) => {
  return (dispatch) => {
    dispatch(set_active_rpt(wIndex, rIndex, dataset));

    if (!fetchToken) {
      return;
    }

    if (rIndex !== undefined && rIndex !== null && Object.entries(rIndex).length > 0) {
      let clientToken = getClientToken();
      let groups = getSessionClaims()?.groups;
      let creator = false;
      let editor = false;
      let mode = "";
      let createMode = [];
      let editMode = [];
      if (groups !== undefined) {
        createMode = groups.filter((group) => {
          return group === "pbi-creator";
        });
        editMode = groups.filter((group) => {
          return group === "pbi-editor";
        });
      }

      if (createMode.length >= 1) {
        creator = true;
      }

      if (editMode.length >= 1 || createMode.length >= 1) {
        editor = true;
      }
      if (editor || creator) {
        mode = "Edit";
      } else {
        mode = "View";
      }
      let workspace = wIndex.workspaceId;

      // Extract workspace ID from report Web URL if available (Most reliable for Custom Apps)
      if (rIndex.webUrl) {
        const match = rIndex.webUrl.match(/groups\/([a-f0-9-]{36})/i);
        if (match && match[1]) {
          workspace = match[1];
        }
      }

      if (wIndex.type === "customApplication") {
        // Ensure we don't use the Custom App ID (Mongo ID, 24 chars) as the workspace ID. PBI GUIDs are 36 chars.
        if (workspace && workspace.length !== 36) {
          workspace = undefined;
        }
      }

      if (workspace === undefined) {
        if (rIndex.workspaceId !== undefined) {
          workspace = rIndex.workspaceId;
        } else if (rIndex.workspace === undefined) {
          workspace = rIndex.workspaceId;
        } else {
          workspace = rIndex.workspace.workspaceId;
        }
      }

      // Use id or _id as fallback
      const reportId = rIndex.id || rIndex._id;

      if (rIndex.type === "powerBI" || rIndex.type === "fileGroupings") {
        // Validation: PBI Workspace IDs must be GUIDs (36 chars). If we have a MongoID (24 chars),
        // it means we haven't resolved the parent PBI workspace yet.
        // Skip the request here and let the Report component handle the resolution and token request.
        if (workspace && workspace.length === 24) {
        } else {
          // Failure is already recorded in Redux via FETCH_ACCESS_TOKEN_FAILURE.
          Promise.resolve(dispatch(getAccessToken(workspace, reportId, false, rIndex.datasetId, mode))).catch(() => {});
        }
      }
    }
  };
};
export const setActiveWS = (wIndex) => {
  return (dispatch) => {
    dispatch(set_active_ws(wIndex));
  };
};

export const deleteReport = (groupId, reportId) => {
  return (dispatch) => {
    // Set loading immediately so the folder view shows a loading indicator
    // instead of stale contents while the delete + refresh is in flight.
    dispatch(fetchReportListBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/deleteReport`,
        {
          groupId,
          reportId,
          // user: localStorage.getItem("user")
          //idToken: localStorage.getItem("id_token")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        dispatch(deleteReportSuccess(response.data.response, reportId, groupId));
        // Reset dedup flag so getReports actually fetches fresh data and clears loading: true.
        _reportsLoaded = false;
        dispatch(getReports());
      })
      .catch((err) => {
        _reportsLoaded = false;
        dispatch(getReports());
      });
  };
};
export const getNewsFeed = () => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/getNewsFeed`,
        {
          //groupId,
          //reportId
          //idToken: localStorage.getItem("id_token")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        function count(main_str, sub_str) {
          main_str += "";
          sub_str += "";

          if (sub_str.length <= 0) {
            return main_str.length + 1;
          }

          let subStr = sub_str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          return (main_str.match(new RegExp(subStr, "gi")) || []).length;
        }
        let sasToken = response.data.sasClientToken;
        let newsfeed = [];
        response.data.newsFeed.forEach((nf) => {
          let rmCount = count(nf.html, "src='https://");
          let x = 1;

          while (x <= rmCount) {
            let img = nf.html.split("src='")[x].split("'")[0];

            if (img.includes("allclients-staticimages")) {
              nf.html = nf.html.replace(img, img + "?" + response.data.sasAllToken);
            } else {
              nf.html = nf.html.replace(img, img + "?" + sasToken);
            }
            x = x + 1;
          }
          newsfeed.push(nf);
        });
        dispatch(setNewsFeed(newsfeed));
      });
  };
};

export const setDisplayedFeaturedReports = (featured) => {
  return (dispatch) => {
    dispatch(set_displayed_featured_reports(featured));
  };
};
// Monotonic version counter for sFavorites/sRecents — module scope (not
// component state) because multiple, independent things can change this data:
// reportIconList's fetch on workspace switch, powerbi-report's fetch on
// report mount, and saveFavorite's own optimistic-confirmed update. A
// getRecentAndFavReports response is only applied if it's still the most
// recent thing requested — bumped by both a NEW read request being issued
// (so an older, slower read loses to a newer one) and a save succeeding (so
// an older, still-in-flight read doesn't overwrite the just-confirmed save
// with pre-save data). Comparing against state.app.reportGroup doesn't work
// for the first case since that field is "" (not just unset) for a report
// opened via a direct deep link, silently disabling that guard for exactly
// the requests it exists to protect.
let favoritesRecentsVersion = 0;

export const getRecentAndFavReports = (workspace) => {
  return (dispatch) => {
    const requestedReportGroup = workspace.reportGroup;
    // Claim the next version at issue time (not just read it) so this call
    // gets a version number distinct from any other call already in flight —
    // whichever was issued last "owns" the current value, so an older
    // response can never match it, regardless of resolution order.
    const myVersion = ++favoritesRecentsVersion;
    // Returned (rather than left dangling) so a caller that needs to know
    // whether this fetch actually landed — e.g. to retry after a dropped
    // request — can attach its own .then/.catch.
    return axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/getRecentsAndFavsForWorkspace`,
        {
          workspace,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        if (myVersion !== favoritesRecentsVersion) {
          // A newer read was issued, or a favorite was saved, since this
          // request started — that's the more current data now, so discard.
          return;
        }
        dispatch(get_recent_and_fav_reports(response.data.recents, response.data.favs, requestedReportGroup));
      });
  };
};
export const clearRecents = (workspace) => {
  return (dispatch) => {
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/reportList/clearRecents`,
        {
          workspace,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        dispatch(getRecentAndFavReports(workspace));
      });
  };
};
export const setDisplayedRpts = (reports) => {
  return (dispatch) => {
    dispatch({ type: SET_DISPLAYED_REPORTS, reports });
  };
};
export const setDisplayedFiles = (files) => {
  return (dispatch) => {
    dispatch({ type: SET_DISPLAYED_FILES, files });
  };
};

function orderItems(collection, workspaces, activeWS) {
  let col = cloneDeep(collection);
  if (collection.length > 0) {
    let order = [];
    workspaces.forEach((item) => {
      if (item._id === activeWS) {
        order = item.order;
      }
    });
    if (order !== undefined && order.length > 0) {
      var e = [];

      order.forEach((el) =>
        e.push(
          collection.find((e) => {
            return e._id === el;
          }),
        ),
      );

      e = e.filter((item) => {
        return item !== undefined;
      });
      // appsOrder = sortBy(appsOrder, [
      //   function (o) {
      //     return o.appOrder;
      //   }
      // ]);
      collection = e;
    }
  }
  if (collection.length < col.length) {
    let collectionArr = collection.map((item) => {
      return item._id;
    });
    col.forEach((item) => {
      if (!collectionArr.includes(item._id)) {
        collection.push(item);
      }
    });
  }

  return collection;
}
/* -------------------- REDUCER -------------------- */
const reportListReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_REPORT_LIST_BEGIN:
      return { ...state, loading: true, loaded: false, error: null };
    case FETCH_REPORT_LIST_SUCCESS:
      return { ...state, loading: false, loaded: true };
    case FETCH_REPORT_LIST_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.payload.error,
        reportConfig: [],
      };
    case SET_WORKSPACES:
      let nTokens = cloneDeep(state.tokens);
      if (nTokens === null) {
        nTokens = [];
      }
      let tVals = nTokens.map((item) => {
        return item.workspaceName;
      });
      action.tokens.forEach((item) => {
        if (!tVals.includes(item.workspaceName)) {
          nTokens.push(item);
        }
      });

      let newWS = action.payload.map((item) => {
        if (item.type === "customApplication") {
          let nChild = [];
          if (item.groupings.length > 0) {
            nChild = [...item.groupings];
          }
          if (item.applications.length > 0) {
            for (var nApp of item.applications) {
              nApp.type = nApp.title;
              nChild.push(nApp);
            }
          }
          if (item.reports.length > 0) {
            for (var nRep of item.reports) {
              nChild.push(nRep);
            }
          }

          var c = [];
          item.order.forEach((el) => {
            c.push(
              nChild.find((e) => {
                if (e.type === "Files") {
                  return e.type === el;
                } else if (e.type === "grouping") {
                  return e.id === el;
                } else if (e.type === "powerBI" || e.type === "tableau" || e.type === "staticContent") {
                  return e._id === el;
                }
                return false;
              }),
            );
          });

          c = c.filter((item) => {
            return item !== undefined;
          });

          item.children = c;
        } else if (item.type === "tableau") {
          item.children = item.reports;
        }

        return item;
      });

      return {
        ...state,
        //sFavorites: action.favorites,
        workspaces: newWS,
        searchWorkspaces: newWS,
        newRptSearchWorkspaces: newWS,
        loading: false,
        loaded: true,
        tokens: nTokens,
      };
    case ADD_WORKSPACE_REPORTS: {
      // activeWS may be a workspace object, a string ID (set by setReportGroup → setActiveWS),
      // or an empty object {} (cleared by setActiveReport after a URL change).
      // Resolve it to the full workspace object in all cases.
      let targetWS = state.activeWS;
      if (!targetWS || !targetWS.reports) {
        const wsId = typeof targetWS === "string" ? targetWS : null;
        if (wsId) {
          targetWS = state.workspaces.find((ws) => ws._id === wsId || ws.workspaceId === wsId) || null;
        }
        // Final fallback: find workspace via the report's own workspaceId (clone/save-as path
        // where activeWS was cleared to {} by the URL-change detection in Report.componentDidUpdate).
        // Note: use !targetWS?.reports not !targetWS — {} is truthy so !{} is false.
        if (!targetWS?.reports && action.report?.workspaceId) {
          targetWS = state.workspaces.find((ws) => ws.workspaceId === action.report.workspaceId) || null;
        }
        // Secondary fallback: match via the report's workspace ObjectId ref (for cloned reports
        // where workspaceId may not be populated but workspace ObjectId ref is available).
        if (!targetWS?.reports && action.report?.workspace) {
          const wsRef = action.report.workspace?.toString?.() ?? String(action.report.workspace);
          targetWS = state.workspaces.find((ws) => ws._id === wsRef) || null;
        }
      }
      if (!targetWS || !targetWS.reports) {
        return {
          ...state,
          loading: false,
          loaded: true,
        };
      }
      // Create new objects/arrays to avoid mutating state directly.
      // Use _id / workspaceId for matching — workspace objects do not have a plain .id field.
      // Also update `children` — generateDisplayRpts reads .children not .reports.
      // For standard powerBI workspaces children ≈ reports; for customApplication the new
      // report is appended to the end (ordering is fixed on the next full getReports refresh).
      const updatedReports = [...targetWS.reports, action.report];
      const updatedChildren = Array.isArray(targetWS.children) ? [...targetWS.children, action.report] : targetWS.children;
      const updatedTargetWS = { ...targetWS, reports: updatedReports, children: updatedChildren };
      const updatedWorkspacesRep = state.workspaces.map((ws) =>
        ws._id === updatedTargetWS._id || (ws.workspaceId && ws.workspaceId === updatedTargetWS.workspaceId)
          ? updatedTargetWS
          : ws,
      );

      return {
        ...state,
        activeWS: updatedTargetWS,
        workspaces: updatedWorkspacesRep,
        searchWorkspaces: updatedWorkspacesRep,
        newRptSearchWorkspaces: updatedWorkspacesRep,
        loading: false,
        loaded: true,
      };
    }
    case SET_SEARCH_WS:
      return {
        ...state,
        searchWorkspaces: action.payload,
      };
    case SET_NEW_RPT_SEARCH_WS:
      return {
        ...state,
        newRptSearchWorkspaces: action.payload,
      };
    case SET_ACTIVE_RPT:
      let activeDataset = action.activeDataset;
      if (activeDataset === -1 && action.activeReport !== undefined && action.activeReport.datasetId !== -1) {
        activeDataset = action.activeReport.datasetId;
      }

      return {
        ...state,
        activeReport: action.activeReport,
        activeWS: action.activeWS,
        reportView: "default",
        activeDataset,
      };
    case SET_ACTIVE_WS:
      return {
        ...state,
        activeWS: action.activeWS,
      };
    case DELETE_REPORT_SUCCESS:
      let updatedWorkspaces = state.workspaces.map((workspace) => {
        const isTarget = String(workspace._id) === String(action.payload._id);
        if (!isTarget) return workspace;
        // Filter the deleted report out of both arrays while preserving the workspace object.
        // Do NOT replace with action.payload (raw server response) — it lacks the derived
        // `children` array built by SET_WORKSPACES that generateDisplayRpts reads from.
        const updatedReports = removeDeletedReportFromCollection(
          workspace.reports,
          action.deletedReportId,
          action.deletedWorkspaceId,
        );
        const updatedChildren = removeDeletedReportFromCollection(
          workspace.children,
          action.deletedReportId,
          action.deletedWorkspaceId,
        );
        return { ...workspace, reports: updatedReports, children: updatedChildren };
      });

      const updatedFavorites = removeDeletedReportFromCollection(
        state.favorites,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedSFavorites = removeDeletedReportFromCollection(
        state.sFavorites,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedRecents = removeDeletedReportFromCollection(
        state.recents,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedSRecents = removeDeletedReportFromCollection(
        state.sRecents,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedFeatured = removeDeletedReportFromCollection(
        state.featured,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedSFeatured = removeDeletedReportFromCollection(
        state.sFeatured,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedOriginalFeatured = removeDeletedReportFromCollection(
        state.originalFeatured,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedDisplayRpts = removeDeletedReportFromCollection(
        state.displayRpts,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );
      const updatedSDisplayRpts = removeDeletedReportFromCollection(
        state.sDisplayRpts,
        action.deletedReportId,
        action.deletedWorkspaceId,
      );

      return {
        ...state,
        searchWorkspaces: updatedWorkspaces,
        workspaces: updatedWorkspaces,
        newRptSearchWorkspaces: updatedWorkspaces,
        favorites: updatedFavorites,
        sFavorites: updatedSFavorites,
        recents: updatedRecents,
        sRecents: updatedSRecents,
        featured: updatedFeatured,
        sFeatured: updatedSFeatured,
        originalFeatured: updatedOriginalFeatured,
        displayRpts: updatedDisplayRpts,
        sDisplayRpts: updatedSDisplayRpts,
      };
    case SET_SEARCH_LANDING_PAGE:
      let newRecs = cloneDeep(action.recents);
      let aFav = cloneDeep(action.favorites);
      let neFeatured = cloneDeep(action.featured);
      newRecs = orderItems(newRecs, state.workspaces, state.activeWS);
      aFav = orderItems(aFav, state.workspaces, state.activeWS);
      neFeatured = orderItems(neFeatured, state.workspaces, state.activeWS);

      return {
        ...state,
        sFavorites: aFav,
        sRecents: newRecs,
        sFeatured: neFeatured,
        sDisplayRpts: action.displayRpts,
        sDisplayFiles: action.displayFiles !== undefined ? action.displayFiles : state.sDisplayFiles,
      };
    case FETCH_LANDING_PAGE_BEGIN:
      return { ...state, iconListLoaded: false, iconListLoading: true };
    case FETCH_LANDING_PAGE_FAILURE:
      // Reset both flags so the componentDidUpdate retry guard (!iconListLoading && !iconListLoaded)
      // can fire again and re-call getLandingPage() on the next render cycle.
      return { ...state, iconListLoaded: false, iconListLoading: false };
    case SET_FAVORITE_RPT:
      let newFavs = cloneDeep(state.favorites);
      let favCheck = [];
      if (action.payload.id.includes("-")) {
        favCheck = newFavs.map((item) => {
          return item.id;
        });
      } else {
        favCheck = newFavs.map((item) => {
          return item._id;
        });
      }
      const isFavPresent = favCheck.includes(action.payload.id);
      // Apply the caller's explicit add/remove intent (matches the server's
      // idempotent saveFavorites semantics) instead of toggling on current
      // presence — two "add" saves resolving out of order (e.g. a rapid
      // double-click) must not desync sFavorites from the server by having
      // the second dispatch flip an already-added item back off.
      if (!action.remove && !isFavPresent) {
        newFavs.push({ id: action.payload.id, _id: action.payload._id });
      } else if (action.remove && isFavPresent) {
        let idx = -1;
        newFavs.forEach((item, index) => {
          if (action.payload.id.includes("-")) {
            if (item.id === action.payload.id) {
              idx = index;
            }
          } else if (item._id === action.payload.id) {
            idx = index;
          }
        });

        newFavs.splice(idx, 1);
      }
      newFavs = orderItems(newFavs, state.workspaces, state.activeWS);
      return {
        ...state,
        sFavorites: newFavs,
        favorites: newFavs,
      };
    case FETCH_LANDING_PAGE_SUCCESS:
      //featured can get set before this returns success ... do not void those changes

      let newTokens = cloneDeep(state.tokens);
      if (newTokens === null) {
        newTokens = [];
      }
      let tokenVals = newTokens.map((item) => {
        return item.workspaceName;
      });
      action.tokens.forEach((item) => {
        if (!tokenVals.includes(item.workspaceName)) {
          newTokens.push(item);
        }
      });

      if (state.featured.length > 0) {
        return {
          ...state,
          // sFavorites: action.favorites,
          // sRecents: action.recents,
          //sFeatured: action.featured,
          // favorites: action.favorites,
          // recents: action.recents,
          //featured: action.featured,
          originalFeatured: action.featured,
          iconListLoaded: true,
          iconListLoading: false,
          tokens: newTokens,
        };
      } else {
        let newFeatured = action.featured.filter((item) => {
          if (item.workspace !== undefined && item.workspace.workspaceId !== undefined) {
            return item.workspace.workspaceId === state.activeWS;
          } else {
            return item.workspaceId === state.activeWS;
          }
        });

        newFeatured = orderItems(newFeatured, state.workspaces, state.activeWS);

        return {
          ...state,
          // sFavorites: action.favorites,
          // sRecents: action.recents,
          sFeatured: newFeatured,
          // favorites: action.favorites,
          // recents: action.recents,
          featured: newFeatured,
          originalFeatured: newFeatured,
          iconListLoaded: true,
          iconListLoading: false,
          tokens: newTokens,
        };
      }
    case FETCH_APP_MENU_BEGIN:
      return { ...state, appMenuLoaded: false, appMenuLoading: true };
    case FETCH_APP_MENU_SUCCESS:
      return {
        ...state,
        appMenu: action.payload,
        searchAppMenu: action.payload,
        appMenuLoaded: true,
        appMenuLoading: false,
      };
    case SET_NEWS_FEED:
      return {
        ...state,
        newsFeed: action.newsFeed,
      };
    case SET_REPORT_VIEW:
      return { ...state, reportView: action.payload };
    case SET_DISPLAYED_FEATURED_REPORTS:
      let newFeatured = cloneDeep(action.featured);
      newFeatured = newFeatured.map((rpt) => {
        if (rpt.img !== undefined && rpt.img !== "") {
          let ws = "";

          if (rpt.type.match(/Grouping/i)) {
            ws = rpt.path
              .split("/")[0]
              .toLowerCase()
              .replace(/[^a-zA-Z0-9]*/g, "");
          } else {
            if (rpt.workspace !== undefined && rpt.workspace.name !== undefined) {
              ws = rpt.workspace.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            } else if (rpt.workspaceName !== undefined) {
              ws = rpt.workspaceName.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            } else {
              for (var wItem of state.workspaces) {
                if (wItem._id === rpt.workspace) {
                  ws = wItem.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
                }
              }
            }
          }

          state.tokens.forEach((token) => {
            if (token.workspaceName === ws && !rpt.img.includes("?st=")) {
              rpt.img = rpt.img + "?" + token.sasToken;
            } else if (token.workspaceName === ws) {
              rpt.img = rpt.img.split("?st=")[0] + "?" + token.sasToken;
            }
          });
        }

        return rpt;
      });

      newFeatured = orderItems(newFeatured, state.workspaces, state.activeWS);

      return {
        ...state,
        featured: newFeatured,
        sFeatured: newFeatured,
      };
    case GET_RECENT_AND_FAV_REPORTS:
      let newRecents = cloneDeep(action.recents);
      newRecents = newRecents.map((rpt) => {
        if (rpt.img !== undefined && rpt.img !== "") {
          let ws = "";
          if (rpt.type.match(/Grouping/i)) {
            ws = rpt.path
              .split("/")[0]
              .toLowerCase()
              .replace(/[^a-zA-Z0-9]*/g, "");
          } else {
            if (rpt.workspaceName === undefined) {
              ws = rpt.workspace.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            } else {
              ws = rpt.workspaceName.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            }
          }

          state.tokens.forEach((token) => {
            if (token.workspaceName === ws && !rpt.img.includes("?st=")) {
              rpt.img = rpt.img + "?" + token.sasToken;
            } else if (token.workspaceName === ws) {
              rpt.img = rpt.img.split("?st=")[0] + "?" + token.sasToken;
            }
          });
        }

        return rpt;
      });
      let nFavs = cloneDeep(action.favs);
      nFavs = nFavs.map((rpt) => {
        if (rpt.img !== undefined && rpt.img !== "") {
          let ws = "";
          if (rpt.type.match(/Grouping/i)) {
            ws = rpt.path
              .split("/")[0]
              .toLowerCase()
              .replace(/[^a-zA-Z0-9]*/g, "");
          } else {
            if (rpt.workspaceName === undefined) {
              ws = rpt.workspace.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            } else {
              ws = rpt.workspaceName.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            }
          }

          state.tokens.forEach((token) => {
            if (token.workspaceName === ws && !rpt.img.includes("?st=")) {
              rpt.img = rpt.img + "?" + token.sasToken;
            } else if (token.workspaceName === ws) {
              rpt.img = rpt.img.split("?st=")[0] + "?" + token.sasToken;
            }
          });
        }

        return rpt;
      });

      newRecents = orderItems(newRecents, state.workspaces, state.activeWS);
      nFavs = orderItems(nFavs, state.workspaces, state.activeWS);
      return {
        ...state,
        recents: newRecents,
        sRecents: newRecents,
        favorites: nFavs,
        sFavorites: nFavs,
        sFavoritesReportGroup: action.reportGroup,
      };
    case SET_DISPLAYED_REPORTS:
      let newRpts = cloneDeep(action.reports);

      newRpts = newRpts.map((rpt) => {
        if (rpt.img !== undefined && rpt.img !== "") {
          let ws = "";
          if (rpt.path !== undefined && rpt.path.split("/").length >= 3) {
            ws = rpt.path
              .split("/")[0]
              .toLowerCase()
              .replace(/[^a-zA-Z0-9]*/g, "");
          } else if (rpt.type.match(/Grouping/i)) {
            ws = rpt.path
              .split("/")[0]
              .toLowerCase()
              .replace(/[^a-zA-Z0-9]*/g, "");
          } else {
            if (rpt.workspace !== undefined && rpt.workspace !== null && rpt.workspace.name !== undefined) {
              ws = rpt.workspace.name.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            } else {
              ws = rpt.workspaceName.toLowerCase().replace(/[^a-zA-Z0-9]*/g, "");
            }
          }

          state.tokens.forEach((token) => {
            if (token.workspaceName === ws && !rpt.img.includes("?st=")) {
              rpt.img = rpt.img + "?" + token.sasToken;
            } else if (token.workspaceName === ws) {
              rpt.img = rpt.img.split("?st=")[0] + "?" + token.sasToken;
            }
          });
        }

        return rpt;
      });

      return {
        ...state,
        sDisplayRpts: newRpts,
        displayRpts: newRpts,
      };
    case SET_DISPLAYED_FILES:
      // let newFiles = cloneDeep(action.files);
      return { ...state, sDisplayFiles: action.files, displayFiles: action.files };
    case SET_REPORTING_APPS:
      return {
        ...state,
        homePageReportingApps: action.payload,
      };
    case SET_RA_FOLDER_STATUS:
      return { ...state, hPRAppsFolderStatus: action.payload };
    case SET_DASHBOARD_RPTS:
      return { ...state, dashboardReports: action.payload };
    default:
      return state;
  }
};

export default reportListReducer;
